<template>
    <span style="display: none" aria-hidden="true" />
</template>

<script>
import {
    normalizeDockZone,
    normalizePanelKind,
    DOCK_ZONES_LIST,
    PANEL_KIND_CENTER,
    PANEL_KIND_TOOL,
} from './constants.js';
import { normalizeZoneList } from './panelZoneConstraints.js';

/**
 * Declarative panel definition for MultipanelDockLayout.
 * Renders no UI; content is mounted into the dock leaf by the parent layout.
 */
export default {
    name: 'MultipanelDockPanel',

    props: {
        id: { type: String, required: true },
        title: { type: String, default: '' },
        icon: { type: String, default: null },
        pinned: { type: Boolean, default: true },
        closable: { type: Boolean, default: true },
        tabOnly: { type: Boolean, default: false },
        ephemeral: { type: Boolean, default: false },
        listInPanelsMenu: { type: Boolean, default: null },
        allowedZones: { type: [String, Array], default: null },
        disallowedZones: { type: [String, Array], default: null },
        /** Initial dock region: left | top | right | bottom | center (case-insensitive). */
        defaultZone: {
            type: String,
            default: null,
            validator: (v) => {
                if (v == null || v === '') return true;
                return DOCK_ZONES_LIST.includes(String(v).toLowerCase());
            },
        },
        /** center = editor-style (top tabs); tool = properties/console-style (bottom tabs when grouped). */
        panelKind: {
            type: String,
            default: null,
            validator: (v) => {
                if (v == null || v === '') return true;
                return [PANEL_KIND_CENTER, PANEL_KIND_TOOL].includes(v);
            },
        },
        /** Alias for defaultZone. */
        initialZone: {
            type: String,
            default: null,
            validator: (v) => {
                if (v == null || v === '') return true;
                return DOCK_ZONES_LIST.includes(String(v).toLowerCase());
            },
        },
    },

    inject: {
        multipanelDockLayout: { default: null },
        multipanelDockInheritedZone: { default: null },
        multipanelDockInheritedRestrictions: { default: null },
        multipanelDockTabGroupId: { default: null },
        multipanelDockZoneBlockKey: { default: null },
    },

    computed: {
        resolvedZone() {
            const raw =
                this.initialZone ??
                this.multipanelDockInheritedZone ??
                this.defaultZone;
            return normalizeDockZone(raw);
        },
        resolvedPanelKind() {
            return normalizePanelKind(this.panelKind, this.resolvedZone);
        },
        resolvedListInPanelsMenu() {
            if (this.listInPanelsMenu != null) return this.listInPanelsMenu;
            return !this.ephemeral;
        },
    },

    watch: {
        resolvedZone() {
            this.register();
        },
        resolvedPanelKind() {
            this.register();
        },
    },

    mounted() {
        this.register();
    },

    beforeUnmount() {
        this.multipanelDockLayout?.unregisterPanel?.(this.id);
    },

    methods: {
        register() {
            if (!this.multipanelDockLayout) {
                console.warn(
                    'MultipanelDockPanel must be a child of MultipanelDockLayout',
                );
                return;
            }
            const slot = this.tabOnly ? null : this.$slots.default;
            this.multipanelDockLayout.registerPanel({
                id: this.id,
                title: this.title,
                icon: this.icon,
                pinned: this.pinned,
                closable: this.closable,
                tabOnly: this.tabOnly,
                ephemeral: this.ephemeral,
                listInPanelsMenu: this.resolvedListInPanelsMenu,
                allowedZones: normalizeZoneList(this.allowedZones),
                disallowedZones: normalizeZoneList(this.disallowedZones),
                inheritedZonePolicy: this.multipanelDockInheritedRestrictions,
                defaultZone: this.resolvedZone,
                panelKind: this.resolvedPanelKind,
                default: slot,
                tabGroupId: this.multipanelDockTabGroupId,
                zoneBlockKey: this.multipanelDockZoneBlockKey,
            });
        },
    },
};
</script>
